import { createContext, useCallback, useContext, useState } from 'react';

export type MetadataMorePanelState = {
  fullMetadataId: string;
  anchorEl: EventTarget & Element;
};

export type MetadataMoreCtxType = {
  metadataMorePanelState: MetadataMorePanelState | null;
  setMetadataMorePanelState: (metadataMorePanelState: MetadataMorePanelState | null) => void;
};
const MetadataMoreCtx = createContext<MetadataMoreCtxType>({
  metadataMorePanelState: null,
  setMetadataMorePanelState: () => {
    throw new Error('setMetadataMorePanelState must be used within a MetadataMoreCtxProvider');
  },
});

export const MetadataMoreCtxProvider = ({ children }: { children: React.ReactNode }) => {
  const [metadataMorePanelState, setMetadataMorePanelState] =
    useState<MetadataMorePanelState | null>(null);
  return (
    <MetadataMoreCtx.Provider value={{ metadataMorePanelState, setMetadataMorePanelState }}>
      {children}
    </MetadataMoreCtx.Provider>
  );
};

export const useMetadataMoreCtx = () => {
  const ctx = useContext(MetadataMoreCtx);
  if (!ctx) {
    throw new Error('useMetadataMoreCtx must be used within a MetadataMoreCtxProvider');
  }
  const { setMetadataMorePanelState, metadataMorePanelState } = ctx;

  const handleMetadataMoreClick = useCallback(
    ({
      event,
      fullMetadataId,
    }: {
      event: React.MouseEvent | React.TouchEvent;
      fullMetadataId: string;
    }) => {
      setMetadataMorePanelState({
        fullMetadataId,
        anchorEl: event.currentTarget,
      });
    },
    [setMetadataMorePanelState],
  );

  const handleMetadataMoreClose = useCallback(() => {
    setMetadataMorePanelState(null);
  }, [setMetadataMorePanelState]);

  return {
    metadataMorePanelState,
    handleMetadataMoreClick,
    handleMetadataMoreClose,
  };
};
